import { Cols, TransactionType, PriceCols, CashEqRegex, ICompValue, ICompValue2, TFilter, ESectors, Sectors } from "../shared/Consts";
import { TQueryParams } from "../libs/Url";
import Holding from "../models/Holding";
import Path from "../libs/path/Path";
import { Transaction } from "../models/Transaction";
import Utils, { type IYMD } from "../libs/Utils"
import { GICTransaction } from "../models/GICTransaction";
import GICHolding from "../models/GICHolding";

const CurrencyFormat : Map<number, Intl.NumberFormat> = new Map([
    [0, new Intl.NumberFormat("en-CA", {style:"currency", currency:"CAD"})],
    [1, new Intl.NumberFormat("en-US", {style:"currency", currency:"USD"})]
]);
const NumberFormat = new Intl.NumberFormat();
const DAY = 24*3600*1000;
const SixMonth = 182*3600*1000;

export {CurrencyFormat, NumberFormat }
export type TPortRoutes = "add"|"update"|"dividend"|"config"|"reinvest"|""

export type TTransactionFilter = {
    search:string
    start:Date
    end:Date
    actions:TransactionType[]
}

export default class PortUtils {
    static get AddHoldingId() { return "addHolding" }

    static parseHash(hash:string) : {holdingId:string, action:TPortRoutes, transactionId:string} {
        const [_,holdingId,action,transactionId] = hash.split("/")
        return {holdingId:holdingId||"", action:(action||"") as TPortRoutes, transactionId:transactionId||""}
    }
    
    static createHash(holdingId?:string, action?:TPortRoutes, transactionId?:string){
        if (!holdingId) holdingId = ""
        if (!action) action = ""
        if (!transactionId) transactionId = ""
        return `/${holdingId}/${action}/${transactionId}`
    }
    
    static numDates(year:number,month:number) { return Utils.numDates(year,month) }
    static isMutualFund(id:string) { return /\.CF$/.test(id)}
    static diffDate(d0:Date, d1:Date){
        return Math.ceil(Math.abs( (d0.getTime() - d1.getTime())/DAY ))
    }
    static dateAdd(date:Date, diff:IYMD) { return Utils.dateAdd(date, diff)}
    static get SixMonth() { return SixMonth }
    
    static zero(num:number|string, digits:number=2) { return Utils.zero(num, digits) }
    static get WEEK() { return Utils.WEEK }
    static formatDate(fmt:string, date:Date|number) : string {
        const d:Date = typeof(date)=="number" ? new Date(date) : date;
        fmt = fmt.replace("yyyy", String(d.getFullYear()));
        fmt = fmt.replace("MM", PortUtils.zero(d.getMonth()+1));
        fmt = fmt.replace("dd", PortUtils.zero(d.getDate()));
        fmt = fmt.replace("HH", PortUtils.zero(d.getHours()));
        fmt = fmt.replace("mm", PortUtils.zero(d.getMinutes()));
        fmt = fmt.replace("ss", PortUtils.zero(d.getSeconds()));
        fmt = fmt.replace("www", PortUtils.WEEK[d.getDay()]);
        return(fmt);
    }
    static toDate(value:Date|number|undefined){
        if (value) return this.formatDate("yyyy-MM-dd", value)
        else return ""
    }
    static sleep(milli:number) {
        return new Promise(cb => setTimeout(cb,milli))
    }
    static thousands(num:number, decimal:number=3){
        return num?.toFixed?.(decimal).replace(/\B(?=(\d{3})+(?!\d))/g, ",") || ""
    }
    static formatCurrency(currency:number, value:number, prependSign=false, absolute=false) {
        const curFormat = CurrencyFormat.get(currency)
        if (curFormat){
            let val = prependSign ? (value>=0 ? ' + ' : ' - ') : ""
            val += (currency ? "" : "C")
            return val + curFormat.format(prependSign || absolute ? Math.abs(value) : value)
        }
        else return "-"
    }
    static formatNumber(value:number){
        return NumberFormat.format(value)
    }
    static formatPercent(value:number, absolute=false){
        return PortUtils.formatNumber(absolute ? Math.abs(value) : value) + "%"
    }
    static filterByAccount(holdings:Holding[], account:number) : Holding[]{
        return holdings.map(holding => holding.filterByAccount(account)).filter(holding => holding) as Holding[]
    }
    static isZero(num:number) : boolean {
        return Math.abs(num)<0.0000001
    }
    static dateEq(d1:Date, d2:Date){
        return d1.getFullYear()==d2.getFullYear() && d1.getMonth()==d2.getMonth() && d1.getDate()==d2.getDate()
    }
    static label(col:string) : string{
        switch(col){
        case Cols.name:             return "Name";
        case Cols.sector:           return "Sector";
        case Cols.quantity:         return "Qty";
        case Cols.averagePrice:     return "Avg";
        case Cols.holdingCost:      return "Cost";
        case Cols.currentPrice:     return "Price"
        case Cols.holdingValue:     return "H.Value";
        case Cols.dividend:         return "Dividend"
        case Cols.mwPercent:        return "W.Gain";
        case Cols.date:             return "Date";
        case Cols.maturity:         return "Maturity";
        case Cols.total:            return "Total";
        case Cols.accountId:        return "Account";
        case Cols.price:            return "Price";
        case Cols.type:             return "Action";
        case Cols.originalCost:     return "O.Cost";
        case Cols.holdingPercent:   return "H.Gain";
        case Cols.note:             return "Notes";
        case Cols.id:               return "Name";
        case Cols.payout:           return "Payout";
        case Cols.yield:            return "Yield";
        case Cols.a_payout:         return "A.Payout";
        case Cols.n_payout:         return "N.Payout";
        case Cols.ytd_payout:       return "YTD.Pyaout"
        case Cols.gain:             return "T.Gain";
        case Cols.maturityMonth:    return "Month";
        case PriceCols.ticker:      return "Ticker";
        case PriceCols.sector:      return "Sector";
        case PriceCols.frequency:   return "Frequency";
        case Cols.excessDiv:        return "Ex.Div";
        case Cols.profit:           return "Profit";
        case Cols.ugain:            return "U.Gain";
        case Cols.holdingPortion:   return "Holding";
        case Cols.balance:          return "Balance";
        default: return col;
        }        
    }
    static hint(col:string) : string {
        switch(col){
        case Cols.gain:             return "Unrealized gain + Profit"
        case Cols.originalCost:     return "Cost excluding reinvestments"
        case Cols.holdingCost:      return "Cost including reinvestments"
        case Cols.mwPercent:        return "Money weighted performance:\n(Sum of all transactions + Holding value)/Initial investment"
        case Cols.holdingPercent:   return "Holding value/Holding costs"
        case Cols.profit:           return "Sum of dividends and gains from disposition"
        case Cols.ugain:            return "Unrealized Gain = Holding Value - Holding Value"
        case Cols.excessDiv:        return "Total dividend - reinvest"
        default: return ""
        }
    }

    static cellValue(col:string,value:string|number,ref:{currency:number,value?:any}) : string|number {
        if (ref.value instanceof Function) return ref.value(col)
        else switch(col){
        case Cols.yield:
        case Cols.mwPercent:
        case Cols.holdingPercent:           return PortUtils.formatPercent(value as number);
        default:
            if (typeof(value)=="number")    return PortUtils.formatCurrency(ref.currency, value)
            else                            return value;
        }
    }
    static cellClass(col:string,value:string|number) : string {
        switch(col){
        case Cols.name:
        case Cols.date:
        case Cols.maturityMonth:
        case Cols.maturity:                 return "nowrap"
        case Cols.price:
        case Cols.sector:
        case Cols.averagePrice:
        case Cols.originalCost:
        case Cols.currentPrice:
        case Cols.quantity:
        case Cols.yield:
        case Cols.holdingCost:
        case Cols.holdingPortion:
        case Cols.holdingValue:             return "";
        case Cols.ytd_payout:
        case Cols.a_payout:
        case Cols.n_payout:
        case Cols.payout:
        case Cols.dividend:                 return "blue";
        default:
            if (typeof(value)=="number") return value>=0 ? "green" : "red"
            else return ""
        }
    }
    static sortClass(col:string,sort:string) : string {
        if (col==sort) return "sort asc"
        else if ("-"+col==sort) return "sort dec";
        else return "";        
    }
    static onSort(col:string, current:string, noQuery:boolean=false) { 
        let newSort;
        if (col==current) newSort = "-"+col;
        else if ("-"+col==current) newSort = col;
        else newSort = "-"+col;
        if (!noQuery) Path.i.setParams({sort:newSort})
        return newSort
    }
    static isCashAction(type:TransactionType){
        return type==TransactionType.adjust || type==TransactionType.dividend || type==TransactionType.interest
    }
    static frequency(ticker:string) : "m"|"q"|"s"|"a" {
        return (/^SAVE|^GIC/.test(ticker)) ? "m" : "q"
    }
    static get disableAutoComplete(){
        return { inputProps: {autoComplete:"off","aria-autocomplete":"none"} } as any;
    }
    static holdingSorter(sort:string, dir:number) {
        return (l:ICompValue,r:ICompValue) => l.compValue(sort) < r.compValue(sort) ? -dir : dir;
    }
    static transSorter(sort:string, dir:number=0){
        return (l:ICompValue2,r:ICompValue2) => {
            const left = l.compValue(sort), right = r.compValue(sort)
            if (left==right) {
                if (sort!=Cols.date) return l.date < r.date ? -dir : dir
                else return l.quantity>r.quantity ? -dir : dir
            }
            else return left < right ? -dir : dir;
        }
    }
    static transSortP(t1:Transaction, t2:Transaction){
        const left = t1.compValue(Cols.date), right = t2.compValue(Cols.date)
        if (left==right) return (t1.quantity>t2.quantity) ? -1 : 1
        else return left < right ? -1 : 1
    }
    static sector(sector:ESectors){return Sectors.find(s => s.id==sector)?.text || "N/A"}

    static get account(){ 
        const account = parseInt(Path.i.query["account"] as string)
        if (isNaN(account)) return -1
        else return account
    }
    static get currency(){ 
        const currency = parseInt(Path.i.query["currency"] as string)
        if (isNaN(currency)) return -1
        else return currency
    }
    static page(key:string) {
        const page = parseInt(Path.i.query[key] as string)
        if (isNaN(page)) return 0;
        else return page;
    }
    static get showDisposed(){
        const show = parseInt(Path.i.query["show"] as string)
        if (isNaN(show) || !show) return false
        else return true
    }
    static get convert(){
        const convert = parseInt(Path.i.query["convert"] as string)
        if (isNaN(convert) || !convert) return false
        else return true
    }
    static get filter(){ return (Path.i.query["search"] as string||"").toLowerCase() }

    static filterSearchTransactions(transactions:Transaction[], filter:TTransactionFilter) {
        if (!filter.search) return transactions
        if (filter.search[0]=="-" || filter.search[0]=="+") {
            const regex = new RegExp(`${filter.search.substring(1)}`, "i")
            if (!regex) return transactions
            else if (filter.search[0]=="-") transactions = transactions.filter(trans => !regex.test(trans.note) && !regex.test(trans.holding.id) && !regex.test(trans.holding.name))
            else transactions = transactions.filter(trans => regex.test(trans.note) || regex.test(trans.holding.id) || regex.test(trans.holding.name))
        }
        else {
            const text = filter.search.toLowerCase();
            transactions = transactions.filter( (trans:Transaction) => trans.filter(text) )
        }
        return transactions
    }

    // filter transactions before applying balance only look at -
    static excludeFilter(transactions:Transaction[], filter:TTransactionFilter) {
        if (filter.search && filter.search[0]=="-") return {transactions,rows:PortUtils.filterTransactions(this.filterSearchTransactions(transactions, filter), filter)}
        else return {transactions,rows:PortUtils.filterTransactions(transactions, filter)}
    }
    static filterTransactions(transactions:Transaction[], filter:TTransactionFilter, isGIC?:boolean) {
        const start = filter.start, end = filter.end
        const isDateFiltered = !!start && !!end && (start.getFullYear()>2000 || end<new Date())
        const isActionFiltered = !!filter.actions && !filter.actions.includes(TransactionType.all)
        const excludeMatured = isGIC && !filter.actions.includes(TransactionType.matured)
        if (!filter.search && !isDateFiltered && !isActionFiltered && excludeMatured) return transactions

        let rows = transactions.slice(0)
        rows = this.filterSearchTransactions(rows, filter)
        if (isDateFiltered) {
            rows = rows.filter( trans => trans.date>=start && trans.date<=end )
        }
        if (isActionFiltered) {
            const allowed = new Set(filter.actions)
            rows = rows.filter( trans => allowed.has(trans.type) )
        }
        if (excludeMatured) {
            const now = new Date()
            rows = rows.filter(trans => (trans as GICTransaction).maturity>now)
        }
        return rows            
    }

    static isGICHolding(holding:Holding) : holding is GICHolding {
        return CashEqRegex.test(holding.id)
    }

    static getFilter(query:TQueryParams, defaultAccount?:number) : TFilter {
        let account : number = PortUtils.account;
        if (defaultAccount!==undefined && account<0) account = defaultAccount
        return {
            account,
            currency:PortUtils.currency,
            filter:PortUtils.filter,
            showDisposed:PortUtils.showDisposed,
            regex:PortUtils.filter ? new RegExp(`${PortUtils.filter}`, "i") : /./
        }
    }

    static trimHoldings(holdings:Holding[], end:number){
        const ret:Holding[] = []
        for(const holding of holdings){
            let needsTrim = false
            for(const trans of holding.transactions){
                if (trans.date.getTime()>end) {
                    needsTrim = true
                    break;
                }
            }
            if (needsTrim) {
                const clone = this.trimTransactions(holding, end)
                if (!PortUtils.isZero(clone.quantity)) ret.push(clone)
            }
            else ret.push(holding)
        }
        return ret
    }

    static trimTransactions(holding:Holding, end:number){
        return holding.createHolding(holding.transactions.filter(trans => trans.date.getTime()<end))
    }

    static toCSV(rows:Holding[]|Transaction[], cols:string[]) {
        return new Blob([rows.map(row => cols.map(col => row.value(col)).join(",")).join("\n")],  {type:"text/csv;charset=utf-8"})
    }
}

if (typeof(window)!="undefined") (window as any).utils = Utils
